// src/lib/webVitals.ts
// Tracks Core Web Vitals (LCP, FID, CLS, FCP, TTFB) and reports them to /api/client-error.
// Uses the native PerformanceObserver API to avoid a heavy third-party dependency.
// All reporting is fire-and-forget and never throws.

type VitalRating = "good" | "needs-improvement" | "poor";

interface VitalEntry {
  metric: string;
  value: number;
  rating: VitalRating;
}

// Thresholds from https://web.dev/vitals/
const THRESHOLDS: Record<string, [number, number]> = {
  LCP: [2500, 4000],   // ms
  FID: [100, 300],     // ms
  CLS: [0.1, 0.25],    // unitless
  FCP: [1800, 3000],   // ms
  TTFB: [800, 1800],   // ms
  INP: [200, 500],     // ms (Interaction to Next Paint, replaces FID)
};

function rate(metric: string, value: number): VitalRating {
  const [good, poor] = THRESHOLDS[metric] ?? [Infinity, Infinity];
  if (value <= good) return "good";
  if (value <= poor) return "needs-improvement";
  return "poor";
}

function getApiBase(): string {
  const appConfigBase =
    (window as Window & { APP_CONFIG?: { VITE_BACKEND_URL?: string } }).APP_CONFIG?.VITE_BACKEND_URL;
  const envApiBase = (import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE;
  const envBackendUrl = (import.meta as { env?: Record<string, string> }).env?.VITE_BACKEND_URL;

  return (
    (window as Window & { __SCC_API_BASE__?: string }).__SCC_API_BASE__ ??
    appConfigBase ??
    envApiBase ??
    envBackendUrl ??
    ""
  );
}

function send(entry: VitalEntry): void {
  try {
    const apiBase = getApiBase();
    fetch(`${apiBase}/api/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "web_vital",
        metric: entry.metric,
        value: Math.round(entry.value * 1000) / 1000, // 3 decimal places
        rating: entry.rating,
        route: window.location.pathname,
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never throw
  }
}

function observe(_metric: string, entryTypes: string[], handler: (entries: PerformanceObserverEntryList) => void): void {
  try {
    const obs = new PerformanceObserver(handler);
    obs.observe({ type: entryTypes[0], buffered: true });
  } catch {
    // PerformanceObserver not supported in this browser
  }
}

// ── Largest Contentful Paint ─────────────────────────────────────────────────
function observeLCP(): void {
  let lastValue = 0;
  observe("LCP", ["largest-contentful-paint"], (list) => {
    const entries = list.getEntries();
    const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
    if (last) lastValue = last.startTime;
  });
  // LCP is finalized on page hide or user interaction
  const report = () => {
    if (lastValue > 0) {
      send({ metric: "LCP", value: lastValue, rating: rate("LCP", lastValue) });
      lastValue = 0; // send only once
    }
  };
  addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") report(); }, { once: true });
  addEventListener("pagehide", report, { once: true });
}

// ── First Input Delay ────────────────────────────────────────────────────────
function observeFID(): void {
  observe("FID", ["first-input"], (list) => {
    const entry = list.getEntries()[0] as PerformanceEntry & { processingStart: number; startTime: number };
    if (entry) {
      const value = entry.processingStart - entry.startTime;
      send({ metric: "FID", value, rating: rate("FID", value) });
    }
  });
}

// ── Cumulative Layout Shift ──────────────────────────────────────────────────
function observeCLS(): void {
  let clsValue = 0;
  let sessionGap = 0;
  let sessionStart = 0;
  const MAX_SESSION_DURATION = 5000;
  const MAX_GAP = 1000;

  observe("CLS", ["layout-shift"], (list) => {
    for (const entry of list.getEntries()) {
      const e = entry as PerformanceEntry & { hadRecentInput: boolean; startTime: number; value: number };
      if (e.hadRecentInput) continue;
      const now = e.startTime;
      if (sessionGap > 0 && now - sessionGap > MAX_GAP || now - sessionStart > MAX_SESSION_DURATION) {
        sessionStart = now;
        clsValue = 0;
      }
      sessionGap = now;
      clsValue += e.value;
    }
  });

  const report = () => {
    if (clsValue > 0) {
      send({ metric: "CLS", value: clsValue, rating: rate("CLS", clsValue) });
      clsValue = 0;
    }
  };
  addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") report(); }, { once: true });
  addEventListener("pagehide", report, { once: true });
}

// ── First Contentful Paint ───────────────────────────────────────────────────
function observeFCP(): void {
  observe("FCP", ["paint"], (list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === "first-contentful-paint") {
        send({ metric: "FCP", value: entry.startTime, rating: rate("FCP", entry.startTime) });
        break;
      }
    }
  });
}

// ── Time to First Byte ───────────────────────────────────────────────────────
function observeTTFB(): void {
  try {
    const [nav] = performance.getEntriesByType("navigation") as (PerformanceEntry & { responseStart: number })[];
    if (nav && nav.responseStart > 0) {
      send({ metric: "TTFB", value: nav.responseStart, rating: rate("TTFB", nav.responseStart) });
    }
  } catch {
    // not supported
  }
}

// ── INP (Interaction to Next Paint, Chrome 96+) ───────────────────────────────
function observeINP(): void {
  observe("INP", ["event"], (list) => {
    let worst = 0;
    for (const entry of list.getEntries()) {
      const e = entry as PerformanceEntry & { duration: number };
      if (e.duration > worst) worst = e.duration;
    }
    if (worst > 0) {
      send({ metric: "INP", value: worst, rating: rate("INP", worst) });
    }
  });
}

export function initWebVitals(): void {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return;
  try {
    observeLCP();
    observeFID();
    observeCLS();
    observeFCP();
    observeTTFB();
    observeINP();
  } catch {
    // don't break app startup
  }
}
